import { UserStateService, UsuariosService, AuthService, SistemasService, NotificationsStore } from '@/app/core';
import { NotificationsBellComponent } from '@/app/shared';
import { Sistemas } from '@/app/models';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { SystemStatusService } from '@/app/core/service/system-status.service';
import { USER_DATA } from '@/app/core/keys/keys';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NOTIFICATIONS_INBOX_ROUTE } from '@/app/core/notifications/notifications-navigation.util';
// PrimeNG (standalone) imports
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AppTopbar } from '@/app/layout/component/topbar/app.topbar';

@Component({
    selector: 'app-sections',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, TooltipModule, AppTopbar],
    templateUrl: './sections.component.html',
    styleUrls: ['./sections.component.scss']
})
export class SectionsComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly userStateService = inject(UserStateService);
    private readonly route = inject(ActivatedRoute);
    private readonly usuarioService = inject(UsuariosService);
    private readonly auth = inject(AuthService);
    private readonly sistemasService = inject(SistemasService);
    private readonly systemStatus = inject(SystemStatusService);
    private readonly cdr = inject(ChangeDetectorRef);
    protected readonly notificationsStore = inject(NotificationsStore);

    /** Overlay de pre-aviso visible */
    showMaintenanceOverlay = false;
    /** Segundos restantes para auto-cierre del overlay */
    maintenanceCountdown = 10;
    /** Mensaje del pre-aviso con el tiempo formateado */
    preNoticeMessage = '';
    /** Tiempo restante hasta el mantenimiento (texto legible) */
    timeUntilMaintenance = '';

    private maintenanceInterval?: ReturnType<typeof setInterval>;
    private countdownInterval?: ReturnType<typeof setInterval>;
    private readonly REMINDER_INTERVAL_MS = 5 * 60 * 1000; // re-mostrar cada 5 min

    private buildPreNoticeMessage(): void {
        const pre = this.systemStatus.maintenance?.preNotice;
        if (!pre) return;
        if (pre.scheduledAt) {
            const diffMs = pre.scheduledAt - Date.now();
            const diffMin = Math.max(0, Math.round(diffMs / 60_000));
            const diffSec = Math.max(0, Math.round(diffMs / 1_000));
            if (diffMin >= 60) {
                const h = Math.floor(diffMin / 60);
                const m = diffMin % 60;
                this.timeUntilMaintenance = m > 0 ? `${h}h ${m}min` : `${h}h`;
            } else if (diffMin >= 1) {
                this.timeUntilMaintenance = `${diffMin} minutos`;
            } else {
                this.timeUntilMaintenance = `${diffSec} segundos`;
            }
        } else {
            this.timeUntilMaintenance = `${pre.minutes ?? 30} minutos`;
        }
        this.preNoticeMessage = (pre.message || 'El sistema entrará en mantenimiento en {time}.')
            .replace('{time}', this.timeUntilMaintenance);
    }

    openMaintenanceOverlay(): void {
        this.buildPreNoticeMessage();
        this.maintenanceCountdown = 10;
        this.showMaintenanceOverlay = true;
        this.cdr.markForCheck();
        clearInterval(this.countdownInterval);
        this.countdownInterval = setInterval(() => {
            this.maintenanceCountdown--;
            this.cdr.markForCheck();
            if (this.maintenanceCountdown <= 0) {
                this.dismissMaintenanceOverlay();
            }
        }, 1000);
    }

    dismissMaintenanceOverlay(): void {
        clearInterval(this.countdownInterval);
        this.showMaintenanceOverlay = false;
        this.cdr.markForCheck();
    }

    /** true cuando el mantenimiento está activo Y el usuario NO es root-admin (id=3) */
    get isMaintenanceUser(): boolean {
        if (!this.systemStatus.maintenance?.enabled) return false;
        try {
            const raw = sessionStorage.getItem(USER_DATA) || localStorage.getItem(USER_DATA);
            if (!raw) return true;
            const parsed = JSON.parse(decodeURIComponent(escape(atob(raw))));
            const systems: Array<{ id: number }> = parsed?.systems ?? [];
            return !systems.some((s) => s.id === 3);
        } catch {
            return true;
        }
    }

    sistemasUsuario: (Sistemas & { locked?: boolean })[] = [];
    allSistemas: (Sistemas & { locked?: boolean })[] = [];

    private readonly SYSTEM_DESCRIPTIONS: Record<string, string> = {
        'Calidad':                              'Inspecciones, observaciones y análisis de calidad con dashboards y plantillas configurables.',
        'Mantenimiento':                        'Avisos, checklists y órdenes de mantenimiento preventivo y correctivo de equipos.',
        'Administracion Blister':               'Gestión de usuarios, empresas, permisos de acceso y parámetros del sistema.',
        'Gestión de Riesgos y Sustentabilidad': 'Auditorías M.A.S., control de incidentes y estadísticas de seguridad y salud.',
    };

    getSystemDescription(nombre: string): string {
        return this.SYSTEM_DESCRIPTIONS[nombre] ?? '';
    }

    // Visual-only loading flag to drive skeletons
    loading = true;
    // Datos de bienvenida / usuario
    displayName = '';
    roleLabel = '';
    avatarLetters = '';
    // --- Estado UI avanzado ---
    query = '';
    skeletons = Array.from({ length: 6 });
    currentYear = new Date().getFullYear();
    // Mostrar estado vacío amigable cuando no hay sistemas
    get isEmpty() {
        return !this.loading && (!this.sistemasUsuario || this.sistemasUsuario.length === 0);
    }

    private destroy$ = new Subject<void>();
    private attemptedAll = false;
    private allFetched = false;
    private userFetched = false;

    private hasToken(): boolean {
        return !!(sessionStorage.getItem('access_token') || localStorage.getItem('access_token'));
    }

    ngOnInit() {
        const datosUsuario = this.userStateService.currentUser;
        // Cargar todos los sistemas apenas inicia (lista completa)
        if (this.hasToken()) {
            this.loadAllSistemas();
        } else {
            this.loading = false;
        }

        // Mostrar overlay de pre-aviso si aplica, y re-mostrar cada 5 min
        this.systemStatus.preNoticeEnabled$.pipe(takeUntil(this.destroy$)).subscribe((active) => {
            if (active) {
                this.openMaintenanceOverlay();
                clearInterval(this.maintenanceInterval);
                this.maintenanceInterval = setInterval(() => {
                    if (this.systemStatus.maintenance?.preNotice?.enabled
                        && !this.systemStatus.maintenance?.enabled) {
                        this.openMaintenanceOverlay();
                    }
                }, this.REMINDER_INTERVAL_MS);
            } else {
                // Pre-aviso desactivado (o mantenimiento ya activo): cerrar overlay y limpiar timer
                clearInterval(this.maintenanceInterval);
                this.dismissMaintenanceOverlay();
            }
        });

        // Esperar al resolver antes de obtener datos
        this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
            const context = data['context'];
            if (context) {
                this.userStateService.setContext(context);
                const currentData = this.userStateService.getCurrentValue();
                if (currentData?.systems?.length) {
                    this.sistemasUsuario = currentData.systems;
                }
                this.initUserMeta();
                const menu = context.menu;
                if (!menu || menu.length === 0) {
                    console.error('No hay rutas disponibles en el menú del sistema.');
                }
            }
            // No marcar loading=false aquí si aún no llegaron sistemas (evita flash de vacío tras F5)
            if (this.sistemasUsuario.length) {
                this.loading = false;
            }
        });

        // Suscribirse a cambios futuros
        this.userStateService.userData$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
            if (data?.systems && data.systems.length) {
                this.sistemasUsuario = data.systems;
                this.userFetched = true;
                this.mergeSystems();
                if (this.allFetched) this.finishLoading();
                this.initUserMeta();
            } else if (!data && !this.loading) {
                console.warn('[Sections] userData vacío tras estar listo');
            }
        });

        // Si tras breve delay no hay sistemas pero existe token, forzar carga única
        setTimeout(() => {
            if (!this.sistemasUsuario.length && this.hasToken()) {
                this.reloadSystems(true);
            }
        }, 400);

        // Fallback timeout: si en 1500ms no llegaron sistemas, finalizar loading para mostrar empty o botón reintentar
        setTimeout(() => {
            if (this.loading) {
                this.loading = false;
            }
        }, 1500);
        this.sistemas;
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        clearInterval(this.maintenanceInterval);
        clearInterval(this.countdownInterval);
    }

    get sistemas(): (Sistemas & { locked?: boolean })[] {
        if (this.allSistemas.length) {
            if (!this.userFetched) {
                // Bloquear clics hasta conocer permisos
                return this.allSistemas.map((s) => ({ ...s, locked: true }));
            }
            return this.allSistemas;
        }
        return this.sistemasUsuario;
    }

    navigate(sistemaId: number, locked?: boolean) {
        if (locked) return;
        this.userStateService.getContext(sistemaId).subscribe({
            next: (data) => {
                const sistema = this.sistemas.find((s) => s.id === sistemaId);
                if (!sistema) {
                    console.error('Sistema no encontrado para el ID:', sistemaId);
                    return;
                }
                const destinoPreferido = this.findMenuRuta(data.menu, 'dashboard');
                const destino = destinoPreferido || this.findMenuRuta(data.menu) || null;

                if (!destino) {
                    console.error('No hay rutas disponibles en el menú del sistema.');
                    return;
                }
                const base = `/${sistema.path}`;
                const fullPath = (destino.startsWith('/')) ? `${destino}` : `/${destino}`;
                //console.log('Navegando a:', fullPath);
                this.router.navigate([fullPath.replace(/\/+/g, '/')]);
            },
            error: (error) => {
                console.error('Error al obtener el contexto:', error);
            }
        });
    }

    // Fallback para cargar sistemas desde /usuario/me cuando userData no los trae
    reloadSystems(merge = false) {
        this.loading = true;
        this.usuarioService.getCurrentUserSystems().subscribe({
            next: (sistemas) => {
                if (Array.isArray(sistemas) && sistemas.length) {
                    this.sistemasUsuario = sistemas;
                    if (merge) this.mergeSystems();
                }
                this.loading = false;
                this.initUserMeta();
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    private initUserMeta() {
        const user = this.userStateService.currentUser;
        if (!user) return;
        this.displayName = `${user.nombre} ${user.apellido}`.trim();
        this.avatarLetters = (user.nombre?.[0] || '').toUpperCase() + (user.apellido?.[0] || '').toUpperCase();
        const grupo = user.grupos?.[0];
        this.roleLabel = grupo ? grupo.nombre : 'Usuario';
    }

    goNotifications() {
        void this.router.navigateByUrl(NOTIFICATIONS_INBOX_ROUTE);
    }
    filteredSystems() {
        const q = this.query.trim().toLowerCase();
        let list = this.sistemas || [];
        if (q) {
            list = list.filter((s: any) => s.nombre.toLowerCase().includes(q));
        }
        return list;
    }

    private mergeSystems() {
        if (!this.allSistemas.length || !this.userFetched) return; // esperar a tener permisos
        const permitidos = new Set(this.sistemasUsuario.map((s) => s.id));
        this.allSistemas = this.allSistemas.map((s) => ({ ...s, locked: !permitidos.has(s.id) }));
    }

    private loadAllSistemas() {
        if (this.attemptedAll) return;
        this.attemptedAll = true;
        this.sistemasService.getAllSistemas().subscribe({
            next: (s: any) => {
                this.allSistemas = s as any;
                this.allFetched = true;
                this.mergeSystems();
                if (this.userFetched) this.finishLoading();
            },
            error: () => {
                this.allFetched = true;
                if (this.userFetched) this.finishLoading();
            }
        });
    }

    private finishLoading() {
        if (this.loading) this.loading = false;
    }

    private findMenuRuta(menu: any[], preferredLabel?: string): string | null {
        if (!Array.isArray(menu)) return null;
        // 1) buscar coincidencia por label preferido
        if (preferredLabel) {
            const match = this.findByPredicate(menu, (it: any) => (it?.label || '').toLowerCase() === preferredLabel && !!it?.ruta);
            if (match) return match;
        }
        // 2) primera ruta navegable (profundidad primero)
        const first = this.findByPredicate(menu, (it: any) => typeof it?.ruta === 'string' && it.ruta.trim().length > 0);
        return first || null;
    }

    private findByPredicate(menu: any[], predicate: (item: any) => boolean): string | null {
        for (const item of menu) {
            if (predicate(item)) return item.ruta;
            const children = item?.children || item?.items || [];
            if (Array.isArray(children) && children.length) {
                const child = this.findByPredicate(children, predicate);
                if (child) return child;
            }
        }
        return null;
    }
}
